<template>
  <div class="space-y-4">
    <div>
      <Label class="text-sm font-medium">Image Library</Label>
      <div class="mt-2">
        <Input
          v-model="searchQuery"
          placeholder="Search images..."
          class="w-full"
        />
      </div>
    </div>

    <!-- Upload Section -->
    <div class="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center">
      <Upload class="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
      <p class="text-sm text-muted-foreground mb-2">Drop images or videos here or click to upload</p>
      <Button 
        variant="outline" 
        size="sm" 
        @click="triggerFileInput"
        :disabled="uploading"
      >
        <Upload class="w-4 h-4 mr-2" />
        {{ uploading ? 'Uploading...' : 'Upload Files' }}
      </Button>
      <input
        ref="fileInput"
        type="file"
        multiple
        accept="image/*,video/mp4,video/webm,video/quicktime"
        class="hidden"
        @change="handleFileUpload"
      />
      <div v-if="uploading" class="mt-2">
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div class="bg-primary h-2 rounded-full transition-all" :style="`width: ${uploadProgress}%`"></div>
        </div>
      </div>
    </div>

    <!-- Image Grid -->
    <div class="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
      <div
        v-for="image in filteredImages"
        :key="image.id"
        class="relative group aspect-square bg-muted rounded border transition-colors"
        :class="isDesktop ? 'cursor-pointer hover:border-primary' : 'cursor-grab hover:border-primary'"
        :draggable="!isDesktop"
        @dragstart="handleDragStart($event, image)"
        @click="isDesktop && handleImageSelect(image)"
        @dblclick="!isDesktop && handleImageSelect(image)"
        :title="isDesktop ? 'Click to add to canvas' : 'Drag to canvas or double-click to add'"
      >
        <!-- Show video preview for video files -->
        <video
          v-if="image.mime_type && image.mime_type.startsWith('video/') && getCachedImageUrl(image)"
          :src="getCachedImageUrl(image)"
          class="w-full h-full object-cover rounded"
          muted
          playsinline
          preload="metadata"
          @mouseenter="($event.target as HTMLVideoElement).play()"
          @mouseleave="($event.target as HTMLVideoElement).pause(); ($event.target as HTMLVideoElement).currentTime = 0"
        />
        <!-- Show image if cached, otherwise show placeholder with filename -->
        <img
          v-else-if="getCachedImageUrl(image)"
          :src="getCachedImageUrl(image)"
          :alt="image.original_name"
          class="w-full h-full object-cover rounded"
          @error="handleImageError"
        />
        <div
          v-else
          class="w-full h-full flex items-center justify-center text-xs text-muted-foreground p-2 text-center"
        >
          {{ image.original_name }}
        </div>
        <button
          @click.stop="downloadImage(image)"
          class="absolute top-1 right-7 w-5 h-5 bg-blue-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-blue-600"
          title="Download image"
        >
          <Download class="w-3 h-3" />
        </button>
        <button
          @click.stop="removeImage(image.id)"
          class="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-red-600"
          title="Remove image"
        >
          ×
        </button>
      </div>
    </div>

    <!-- YouTube Section -->
    <div class="border-t pt-4">
      <Label class="text-sm font-medium">YouTube Videos</Label>
      <div class="mt-2 space-y-2">
        <Input
          v-model="youtubeUrl"
          placeholder="Paste YouTube URL..."
          @keyup.enter="addYoutubeVideo"
        />
        <Button
          variant="outline"
          size="sm"
          @click="addYoutubeVideo"
          :disabled="!youtubeUrl.trim()"
        >
          <Youtube class="w-4 h-4 mr-2" />
          Add YouTube Video
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { logger } from '@/lib/logger'
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Upload, Youtube, Download } from 'lucide-vue-next'
import { useImageLibraryStore } from '@/stores/imageLibrary'
import { useImageCacheStore } from '@/stores/imageCache'
import type { ImageLibraryItem } from '@/stores/sceneDisplayConfigs'
import { Platform } from '@/lib/platform'

interface Props {
  sceneDisplayConfigId: string
}
const props = defineProps<Props>()

const emit = defineEmits<{
  addImage: [imageId: string]
  addYoutube: [url: string]
}>()

// Stores
const imageLibraryStore = useImageLibraryStore()
const imageCacheStore = useImageCacheStore()

// State
const searchQuery = ref('')
const youtubeUrl = ref('')
const fileInput = ref<HTMLInputElement>()
const uploading = ref(false)
const uploadProgress = ref(0)

// Detect if running in Tauri desktop
const isDesktop = Platform.isDesktop()

// Computed
const filteredImages = computed(() => {
  if (!searchQuery.value) return imageLibraryStore.images
  
  return imageLibraryStore.searchImages(searchQuery.value)
})

// Cache version reactive dependency - forces re-computation when cache updates
const cacheVersion = computed(() => imageCacheStore.cacheVersion)

// Computed map of image IDs to cached URLs - updates when cache or images change
const imageCachedUrls = computed(() => {
  // Access cacheVersion to create reactive dependency
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  cacheVersion.value
  
  const urlMap = new Map<string, string>()
  for (const image of filteredImages.value) {
    const storagePath = image.preview_storage_path || image.storage_path
    if (storagePath) {
      urlMap.set(image.id, imageCacheStore.getCachedUrlByPath(storagePath))
    }
  }
  return urlMap
})

// Get cached URL for an image preview (uses the reactive computed map)
const getCachedImageUrl = (image: ImageLibraryItem): string => {
  return imageCachedUrls.value.get(image.id) || ''
}

// Trigger preview generation for images that don't have one when they're displayed
const ensurePreviewExists = (image: ImageLibraryItem) => {
  if (!image.preview_storage_path && image.storage_path) {
    // Generate preview in background (non-blocking)
    imageLibraryStore.generatePreviewForExisting(image.id)
  }
}

// Preload visible images when they change
watch(filteredImages, async (images) => {
  if (images.length > 0) {
    // Use preview storage paths for preloading (much faster)
    const paths = images
      .map(img => img.preview_storage_path || img.storage_path)
      .filter(Boolean) as string[]
    
    if (paths.length > 0) {
      await imageCacheStore.preloadImagesByPath(paths, 'user-images')
    }
    
    // Trigger preview generation for images without previews
    for (const img of images) {
      ensurePreviewExists(img)
    }
  }
}, { immediate: true })

// File upload handler
const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  
  if (!files || files.length === 0) return
  
  uploading.value = true
  uploadProgress.value = 0
  
  try {
    // Handle multiple file uploads
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      const isVideo = file.type.startsWith('video/')
      const isImage = file.type.startsWith('image/')
      
      // Validate file type
      if (!isImage && !isVideo) {
        logger.warn(`Skipping unsupported file: ${file.name}`)
        continue
      }
      
      // Validate file size (10MB for images, 50MB for videos)
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024
      if (file.size > maxSize) {
        logger.warn(`File too large: ${file.name} (max ${isVideo ? '50MB' : '10MB'})`)
        continue
      }
      
      const uploadedImage = isVideo 
        ? await imageLibraryStore.uploadVideo(file)
        : await imageLibraryStore.uploadImage(file)
      
      // Immediately preload the new image into cache so it displays right away
      // IMPORTANT: await this to ensure the image is cached before the UI renders it
      const previewPath = uploadedImage.preview_storage_path || uploadedImage.storage_path
      if (previewPath) {
        try {
          await imageCacheStore.getImageByPath(previewPath, 'user-images')
        } catch {
          // Ignore preload errors - the image will load on next access
        }
      }
      
      uploadProgress.value = ((i + 1) / files.length) * 100
    }
  } catch (error) {
    logger.error('Upload failed:', error)
  } finally {
    uploading.value = false
    uploadProgress.value = 0
    // Clear the input
    target.value = ''
  }
}

// Handle drag start for images
const handleDragStart = (event: DragEvent, image: ImageLibraryItem) => {
  event.dataTransfer?.setData('application/json', JSON.stringify({
    type: 'image',
    imageId: image.id,
    url: image.url,
    name: image.original_name
  }))
}

// Handle direct image selection (double-click)
const handleImageSelect = (image: ImageLibraryItem) => {
  emit('addImage', image.id)
}

// Track retries per image to prevent infinite loops
const imageRetryCount = new Map<string, number>()
const MAX_IMAGE_RETRIES = 2

// Handle image load error - invalidate cache and trigger re-fetch (with limit)
const handleImageError = async (event: Event) => {
  const img = event.target as HTMLImageElement
  const originalSrc = img.src
  
  // Show placeholder immediately
  img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBFcnJvcjwvdGV4dD4KPHN2Zz4='
  
  // If this was a blob URL that failed, invalidate the cache entry and retry
  if (originalSrc.startsWith('blob:')) {
    // Find which image this was
    for (const image of filteredImages.value) {
      const cachedUrl = imageCachedUrls.value.get(image.id)
      if (cachedUrl === originalSrc) {
        // Check retry count to prevent infinite loops (Firefox security error issue)
        const retryCount = imageRetryCount.get(image.id) || 0
        if (retryCount >= MAX_IMAGE_RETRIES) {
          logger.warn('🛑 [ImageLibrary] Max retries reached for image:', image.id)
          break
        }
        
        imageRetryCount.set(image.id, retryCount + 1)
        logger.warn(`🔄 [ImageLibrary] Blob URL failed, retry ${retryCount + 1}/${MAX_IMAGE_RETRIES}:`, image.id)
        
        const storagePath = image.preview_storage_path || image.storage_path
        if (storagePath) {
          // Invalidate the cached entry
          await imageCacheStore.invalidateByPath(storagePath)
          // Re-fetch the image
          const newUrl = await imageCacheStore.getImageByPath(storagePath, 'user-images')
          if (newUrl && newUrl !== originalSrc) {
            img.src = newUrl
          }
        }
        break
      }
    }
  }
}

// Add YouTube video
const addYoutubeVideo = () => {
  if (!youtubeUrl.value.trim()) return

  // Basic YouTube URL validation
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
  const match = youtubeUrl.value.match(youtubeRegex)

  if (match) {
    const videoId = match[1]
    const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`
    emit('addYoutube', embedUrl)
    youtubeUrl.value = ''
  } else {
    // Could show error message
    logger.error('Invalid YouTube URL')
  }
}

// Trigger file input click
const triggerFileInput = () => {
  fileInput.value?.click()
}

// Download image to disk
const downloadImage = async (image: ImageLibraryItem) => {
  try {
    const storagePath = image.storage_path
    if (!storagePath) return
    
    // Get the full-resolution image URL (not preview)
    const url = await imageCacheStore.getImageByPath(storagePath, 'user-images')
    if (!url) return
    
    const response = await fetch(url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = image.original_name || image.filename || 'image'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(blobUrl)
  } catch (error) {
    logger.error('Failed to download image:', error)
  }
}

// Remove image from library
const removeImage = async (imageId: string) => {
  try {
    await imageLibraryStore.deleteImage(imageId)
  } catch (error) {
    logger.error('Failed to delete image:', error)
  }
}

// Load images on mount and eagerly when store is accessed
onMounted(async () => {
  // Fetch images immediately, even if tab is not visible yet
  if (imageLibraryStore.images.length === 0) {
    try {
      await imageLibraryStore.fetchImages()
    } catch (error) {
      logger.error('Failed to load images:', error)
    }
  }
})

// Also fetch when the component becomes visible (tab switch)
// This ensures images load even if you navigate to the tab later
if (typeof document !== 'undefined') {
  const handleVisibilityChange = async () => {
    if (!document.hidden && imageLibraryStore.images.length === 0) {
      try {
        await imageLibraryStore.fetchImages()
      } catch (error) {
        logger.error('Failed to load images on visibility change:', error)
      }
    }
  }
  
  document.addEventListener('visibilitychange', handleVisibilityChange)
  
  // Clean up listener
  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
  
  // Return cleanup function for onBeforeUnmount
  onBeforeUnmount(cleanup)
}
</script>